import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mangas } from '@/mocks/mangas';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function buildChapters(total: number) {
  const arr: { no: number; title: string }[] = [];
  for (let i = 1; i <= total; i++) arr.push({ no: i, title: `第${i}话` });
  return arr;
}

export default function ReadPage() {
  const { id = '' } = useParams();
  const manga = mangas.find((m) => m.id === id);
  const chapters = useMemo(() => (manga ? buildChapters(manga.chapters) : []), [manga]);
  const [current, setCurrent] = useState(1);

  useDocumentMeta(
    manga ? `${manga.title} ${chapters[current - 1]?.title || ''} - 在线阅读` : '漫画阅读 - 樱花漫画网',
    manga ? `${manga.title}第${current}话在线阅读，樱花漫画网流畅阅读体验。` : undefined,
  );

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 pt-20 text-center">
          <p className="font-heading text-2xl font-bold text-foreground-900">作品不存在</p>
          <Link to="/category/all" className="mt-6 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600">
            返回漫画列表
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const isFirst = current <= 1;
  const isLast = current >= manga.chapters;

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        {/* 阅读顶栏 */}
        <div className="sticky top-16 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur md:top-[72px]">
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3 md:px-6">
            <Link to={`/manga/${manga.id}`} className="flex items-center gap-2 text-sm font-semibold text-foreground-800 transition hover:text-primary-600">
              <i className="ri-arrow-left-line"></i>
              <span className="hidden sm:inline">返回详情</span>
            </Link>
            <span className="truncate text-sm text-foreground-700">
              {manga.title} · {chapters[current - 1]?.title}
            </span>
            <div className="flex items-center gap-2">
              <select
                value={current}
                onChange={(e) => setCurrent(Number(e.target.value))}
                aria-label="选择章节"
                className="rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-800 outline-none"
              >
                {chapters.map((c) => (
                  <option key={c.no} value={c.no}>{c.title}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 阅读画布 */}
        <section className="mx-auto max-w-4xl px-4 py-8 md:px-6">
          <div className="flex flex-col items-center">
            <img
              src={manga.cover}
              alt={`${manga.title} ${chapters[current - 1]?.title} 阅读页`}
              title={`${manga.title} 第${current}话`}
              className="w-full max-w-xl rounded-lg object-cover"
            />
            <div className="mt-6 flex w-full items-center justify-center gap-4">
              <button
                type="button"
                disabled={isFirst}
                onClick={() => setCurrent((c) => Math.max(1, c - 1))}
                className="inline-flex items-center gap-1 rounded-full bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-700 whitespace-nowrap transition enabled:hover:bg-primary-100 enabled:hover:text-primary-700 disabled:opacity-40"
              >
                <i className="ri-arrow-left-line"></i>
                上一话
              </button>
              <button
                type="button"
                disabled={isLast}
                onClick={() => setCurrent((c) => Math.min(manga.chapters, c + 1))}
                className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition hover:bg-primary-600 disabled:opacity-40"
              >
                下一话
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <p className="mt-4 text-xs text-foreground-400">
              已加载 {manga.title} {chapters[current - 1]?.title}，支持上下页切换，欢迎收藏本作追更后续更新。
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}